import {
    AfterViewInit,
    HostBinding,
    InjectionToken,
    OnDestroy,
    ViewChild,
} from '@angular/core';
import { PageEvent } from '@angular/material/paginator';
import { MapperDefinition } from '@services';
import {
    IFilterModel,
    IGridDataEntry,
    UiGridComponent,
} from '@uipath/angular/components/ui-grid';

import isEqual from 'lodash-es/isEqual';
import {
    BehaviorSubject,
    combineLatest,
    Observable,
    of,
    Subject,
} from 'rxjs';
import {
    map,
    shareReplay,
    startWith,
} from 'rxjs/operators';

export interface IGridSource<TQuery, TResponse> {
    get(query: TQuery): Observable<TResponse>;
}

export interface IODataSource<TEntity, TQuery, TResponse> extends IGridSource<TQuery, TResponse> {
    mapper?: MapperDefinition<TEntity>;
}

export interface GridResult<T> {
    total: number;
    data: T[];
}

export const GRID_SERVICE = new InjectionToken<IGridSource<{}, {}>>('Grid-Data-Source');

export enum GridPageState {
    ready,
    pending,
}

export abstract class GridComponent<TEntity extends IGridDataEntry> implements AfterViewInit, OnDestroy {

    @HostBinding('class.ui-grid-page-state-ready')
    protected get _isInReadyState() {
        return this._state$.getValue() === GridPageState.ready;
    }

    @HostBinding('class.ui-grid-page-state-pending')
    protected get _isInPendingState() {
        return this._state$.getValue() === GridPageState.pending;
    }

    @HostBinding('class.ui-grid-page-loading')
    protected get _isLoading() {
        return this.loading$.getValue();
    }

    public pageSizes = [10, 25, 50];
    public pageSize = 10;

    public data$ = new Subject<TEntity[]>();
    public total$ = new Subject<number>();
    public loading$ = new BehaviorSubject<boolean>(true);
    public hasCustomFilters$?: Observable<boolean>;

    protected _refresh$ = new Subject();
    protected _pagination$?: Observable<PageEvent>;
    protected _search$?: Observable<IFilterModel<TEntity>[]>;
    protected _filters$ = new BehaviorSubject<IFilterModel<TEntity>[]>([]);
    protected _aggregatedFilters$?: Observable<IFilterModel<TEntity>[]>;
    protected _initialGridFilters$ = new BehaviorSubject<IFilterModel<TEntity>[]>([]);
    protected _state$ = new BehaviorSubject<GridPageState>(GridPageState.ready);
    protected _destroyed$ = new Subject();

    protected readonly _defaultPage = {
        length: NaN,
        pageIndex: 0,
        previousPageIndex: NaN,
        pageSize: this.pageSize,
    };

    @ViewChild(UiGridComponent, { static: true })
    protected _grid!: UiGridComponent<TEntity>;

    ngAfterViewInit() {
        this._pagination$ = this._grid.footer ?
            this._grid.footer.pageChange
                .pipe(
                    startWith(this._defaultPage),
                ) :
            of(this._defaultPage);

        this._search$ = this._grid.header ?
            this._grid.header.searchFilter
                .pipe(startWith([])) :
            of([]);

        this._aggregatedFilters$ = combineLatest([
            this._filters$,
            this._grid.filterManager.filter$.pipe(
                map((gridFilters, i) => {
                    if (i === 0) {
                        // on first emit store the initial input filters
                        this._initialGridFilters$.next(gridFilters);
                    }
                    return gridFilters;
                }),
            ),
        ]).pipe(
            map(([pageFilters, gridFilters]) => [...pageFilters, ...gridFilters]),
            shareReplay(1),
        );

        this.hasCustomFilters$ = combineLatest([
            this._grid.filterManager.filter$,
            this._initialGridFilters$,
        ]).pipe(
            map(([currentFilters, initialFilters]) =>
                !isEqual(
                    this._sortByProperty(currentFilters),
                    this._sortByProperty(initialFilters),
                ),
            ),
        );
    }

    ngOnDestroy() {
        this.loading$.complete();
        this._state$.complete();
        this._destroyed$.next();
        this._destroyed$.complete();
    }

    public getItemAtIndex = (index: number) => {
        return this._grid.dataManager.get(index);
    }

    private _sortByProperty(filters: IFilterModel<TEntity>[]): any {
        return filters.sort((a, b) => (a.property > b.property) ? 1 : -1);
    }
}
